import { options } from './compare';
import { MONTH_LONG, addDays, daysBetween, eachDay, fr, longDate, monthSpan, monthOf, weekStart, weekdayShort } from './dates';
import { f0, f1, GREEN, mean, NEUTRAL, RED } from './format';
import { BRISTOL, COMPLETE, INCOMPLETE, LEVELS, LEVEL_COLOURS } from './palette';
import { clampDay, weekName } from './weeks';

// The "Bowel pattern" panel, ported from the design's bowelChart(): one
// column per day (stool-type chips, urgency and straining bars, incomplete
// badges) for a week or a month, an optional compared period below it, and a
// summary table with a CHANGE column. Plain data only; the UI wires the clicks.

export const VIEW_OPTIONS = [['week', 'Week'], ['month', 'Month']];

const monthLabel = (iso) => {
  const { year, month } = monthOf(iso);
  return `${MONTH_LONG[month]} ${year}`;
};

function period(ctx, view, iso) {
  if (view === 'month') {
    const { start, end } = monthSpan(iso);
    return { start, end, label: monthLabel(iso) };
  }
  const start = weekStart(iso);
  return { start, end: addDays(start, 6), label: weekName(ctx, start) };
}

/** `sel` moved one period back or forward, clamped to the window; null past either end. */
function stepped(ctx, view, sel, main, dir) {
  if (dir < 0 ? main.start <= ctx.historyStart : main.end >= ctx.today) return null;
  if (view === 'week') return clampDay(ctx, addDays(sel, 7 * dir));
  const { year, month } = monthOf(sel);
  const day = Number(sel.slice(8, 10));
  const target = new Date(Date.UTC(year, month + dir, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return clampDay(ctx, target.toISOString().slice(0, 10));
}

const rounded = (a, b) => (a == null || b == null ? null : Math.round(a - b));

/** "↑ 12 pp vs previous", coloured; neutral for counts. */
function change(r, unit, word, scored) {
  if (r == null) return { d: '', dFg: NEUTRAL };
  if (r === 0) return { d: `No change${word ? ` vs ${word}` : ''}`, dFg: NEUTRAL };
  return { d: `${r > 0 ? '↑ ' : '↓ '}${Math.abs(r)}${unit}${word ? ` vs ${word}` : ''}`, dFg: !scored ? NEUTRAL : r > 0 ? GREEN : RED };
}

const pcTxt = (v) => (v == null ? '—' : `${Math.round(v)}%`);
const widths = (counts, n) => counts.map((k) => `${n ? (k / n) * 100 : 0}%`);

/**
 * @param {{ sel: string, view?: 'week'|'month', cmp?: 'prev'|'first'|'off' }} state
 */
export function buildBowelPattern(ctx, { sel, view = 'week', cmp: cmpKey = 'prev' }) {
  const main = period(ctx, view, sel);
  let cmp = null;
  if (cmpKey === 'prev') cmp = period(ctx, view, addDays(main.start, -1));
  if (cmpKey === 'first' && ctx.firstLog) cmp = period(ctx, view, ctx.firstLog);
  if (cmp && (cmp.start === main.start || cmp.end < ctx.historyStart)) cmp = null;
  const word = cmpKey === 'first' ? 'first logged' : 'previous';
  const S0 = ctx.span(main.start, main.end);
  const S1 = cmp ? ctx.span(cmp.start, cmp.end) : null;
  const rangeOf = (P) => fr(P.start < ctx.historyStart ? ctx.historyStart : P.start, P.end > ctx.today ? ctx.today : P.end);

  const panel = (P, S, isMain) => {
    const cols = eachDay(P.start, P.end).map((iso, i) => {
      const inR = ctx.inRange(iso);
      const B = inR ? ctx.bowel[iso] || null : null;
      const isSel = iso === sel;
      const u = B ? mean(B.map((m) => m.u)) : null;
      const s = B ? mean(B.map((m) => m.s)) : null;
      const inc = B ? B.filter((m) => m.complete === false).length : 0;
      const isMonday = weekdayShort(iso) === 'Mon';
      const showDate = view === 'week' || isMonday || i === 0 || iso === ctx.today;
      return {
        iso,
        label: `${longDate(iso)} · ${B ? `${B.length} movement${B.length === 1 ? '' : 's'}` : inR ? 'not recorded' : 'outside the fetched year'}`,
        clickable: inR,
        bg: isSel ? '#F6EFEA' : 'transparent',
        bl: view === 'month' && isMonday && i > 0 ? '1px solid #E8E0D9' : '1px solid transparent',
        chips: (B || []).slice().reverse().map((m) => ({
          label: m.ty ? String(m.ty) : '?',
          bg: m.ty ? BRISTOL[m.ty][0] : '#E8E0D9',
          fg: m.ty ? BRISTOL[m.ty][1] : '#3a3a3a',
          ring: m.complete === false ? '0 0 0 2px #fff, 0 0 0 3.5px #C8553D' : 'none',
          tip: `${m.time || 'Time not recorded'} · ${m.ty ? `Type ${m.ty}` : 'Type not recorded'}${m.complete === false ? ' · incomplete' : ''}`,
        })),
        noLog: inR && !B,
        hasU: u != null,
        uH: u == null ? '0px' : `${Math.max(2, (u / 3) * 32)}px`,
        uV: u == null ? '' : f1(u),
        uTip: u == null ? 'No data' : `Urgency avg ${f1(u)}`,
        hasS: s != null,
        sH: s == null ? '0px' : `${Math.max(2, (s / 3) * 32)}px`,
        sV: s == null ? '' : f1(s),
        sTip: s == null ? 'No data' : `Straining avg ${f1(s)}`,
        hasInc: inc > 0,
        inc: String(inc),
        date: showDate ? `${view === 'week' ? `${weekdayShort(iso)} ` : ''}${Number(iso.slice(8, 10))}` : '',
        dFg: !inR ? '#C9C2BC' : isSel ? '#1f1f1f' : iso === ctx.today ? '#8B5A3C' : '#8a8a8a',
        dW: isSel || iso === ctx.today ? 700 : 400,
      };
    });
    const B = S.bowel;
    const dN = isMain && S1 ? change(rounded(B.normal, S1.bowel.normal), ' pp', word, true) : { d: '', dFg: NEUTRAL };
    const dC = isMain && S1 ? change(rounded(B.comfort, S1.bowel.comfort), ' pts', word, true) : { d: '', dFg: NEUTRAL };
    return {
      key: isMain ? 'main' : 'cmp',
      title: P.label,
      range: rangeOf(P),
      tag: isMain ? 'SELECTED' : `COMPARED · ${word.toUpperCase()}`,
      tagBg: isMain ? '#1f1f1f' : '#fff',
      tagFg: isMain ? '#fff' : '#555',
      tagRing: isMain ? 'none' : 'inset 0 0 0 1px #CFC4BB',
      bt: isMain ? 'none' : '1px dashed #E2D3C8',
      pt: isMain ? '0' : '18px',
      stats: [
        { k: 'Normal stools (Type 3–4)', v: B.normal == null ? '—' : `${f0(B.normal)}%`, ...dN },
        { k: 'Bowel comfort', v: B.comfort == null ? '—' : f0(B.comfort), ...dC },
        { k: 'Movements', v: String(B.n), d: '', dFg: NEUTRAL },
      ],
      cols,
    };
  };

  const ROWS = [
    ['Normal stools', 'Share in Bristol Type 3–4', (b) => b.normal, pcTxt, null, ' pp', true],
    ['Bowel comfort', 'Score 0–100 · higher is better', (b) => b.comfort, (v) => (v == null ? '—' : f0(v)), null, ' pts', true],
    ['Movements', 'Logged in period', (b) => b.n, (v) => String(v), null, '', false],
    ['Urgency', 'Share of rated movements with no urgency', (b) => b.noUrg, (v) => `${pcTxt(v)} none`,
      (b) => widths(b.uCounts, b.uCounts.reduce((a, c) => a + c, 0)).map((w, l) => ({ w, bg: LEVEL_COLOURS[l] })), ' pp', true],
    ['Straining', 'Share of rated movements with no straining', (b) => b.noStr, (v) => `${pcTxt(v)} none`,
      (b) => widths(b.sCounts, b.sCounts.reduce((a, c) => a + c, 0)).map((w, l) => ({ w, bg: LEVEL_COLOURS[l] })), ' pp', true],
    ['Complete evacuation', 'Green = complete · Red = incomplete · of movements recorded', (b) => b.complete, pcTxt,
      (b) => widths([b.nc, b.inc], b.known).map((w, i) => ({ w, bg: [COMPLETE, INCOMPLETE][i] })), ' pp', true],
  ];
  const cell = (S, P, [, , fn, fmt, bar], isMovements) => {
    const days = daysBetween(P.start < ctx.historyStart ? ctx.historyStart : P.start, P.end > ctx.today ? ctx.today : P.end) + 1;
    return {
      v: fmt(fn(S.bowel)),
      sub: isMovements ? `${(S.bowel.n / Math.max(1, days)).toFixed(1)} per day` : '',
      hasBar: Boolean(bar),
      segs: bar ? bar(S.bowel) : [],
    };
  };
  const rows = ROWS.map((def, ri) => {
    const [k, kd, fn, , , unit, scored] = def;
    const cells = [cell(S0, main, def, ri === 2)];
    if (S1) cells.push(cell(S1, cmp, def, ri === 2));
    const ch = S1 ? change(rounded(fn(S0.bowel), fn(S1.bowel)), unit, '', scored) : { d: '', dFg: NEUTRAL };
    return { k, kd, cells, d: ch.d, dFg: ch.dFg, hasCh: Boolean(S1) };
  });

  const isWeek = view === 'week';
  return {
    periodLabel: main.label,
    viewOpts: options(VIEW_OPTIONS, view),
    cmpOpts: options([
      ['prev', isWeek ? 'Previous week' : 'Previous month'],
      ['first', isWeek ? 'First logged week' : 'First logged month'],
      ['off', 'None'],
    ], cmpKey),
    prevSel: stepped(ctx, view, sel, main, -1),
    nextSel: stepped(ctx, view, sel, main, 1),
    panels: [panel(main, S0, true), ...(cmp ? [panel(cmp, S1, false)] : [])],
    head: [{ t: main.label, s: rangeOf(main) }, ...(cmp ? [{ t: cmp.label, s: rangeOf(cmp) }] : [])],
    gcols: cmp ? 'minmax(150px,1.2fr) minmax(0,1fr) minmax(0,1fr) minmax(90px,.6fr)' : 'minmax(150px,1.2fr) minmax(0,2fr)',
    hasCmp: Boolean(cmp),
    rows,
    levels: LEVELS.map((label, i) => ({ label, bg: LEVEL_COLOURS[i] })),
    types: [1, 2, 3, 4, 5, 6, 7].map((ty) => ({ label: String(ty), bg: BRISTOL[ty][0], fg: BRISTOL[ty][1] })),
  };
}
